import React, { useState, useEffect, memo, useCallback } from "react";
import { View, Text, Image, Platform } from "react-native";
import StreamingPlateformComponent from "./StreamingPlateformComponent";
import KOBroadcasterComponent from "./KOBroadcasterComponent";
import TwitchConnectedModal from "./TwitchConnectedModal";
import { useStreamingOAuth } from "../../hooks/useStreamingOAuth";
import { toast } from "react-toastify";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import { useWsProvider } from "../../context/NativeApplicationContext";
import { useDeviceSize, DEVICE_SIZES } from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import { CommonButton } from "../../common/CommonButton";

interface PlatformInterface {
  label: string;
  value: string;
}

interface SetupState {
  isConnecting: boolean;
  isDownloading: boolean;
  downloadProgress: number;
  isDownloaded: boolean;
  estimatedDownloadTime: string;
  downloadSpeed: number;
  startTime: number | null;
}

interface StreamSettingsTabComponentProps {
  accessToken: string;
}

const StreamSettingsTabComponent: React.FC<StreamSettingsTabComponentProps> = ({
  accessToken,
}) => {
  const redirectUri = "/settings?tab=streamSettings";

  const {
    isYouTubeConnected,
    isTwitchConnected,
    redirectToYouTubeOAuth,
    redirectToTwitchOAuth,
    loadingState,
    isInitialLoading,
    twitchUsername,
    youtubeUsername,
    squadifiUsername,
    fetchStatus,
  } = useStreamingOAuth(accessToken, redirectUri);

  const { isNativeAppConnected: isKOBroadcasterConnected } = useWsProvider();
  const deviceSize = useDeviceSize();

  const socialMediaPlatforms: PlatformInterface[] = [
    { label: "YouTube", value: "youtube" },
    { label: "Twitch", value: "twitch" },
  ];

  // Calculate if any streaming platform is connected
  const isStreamConnected = isYouTubeConnected || isTwitchConnected;
  const [streamingTooltip, setStreamingTooltip] = useState<boolean>(true);
  const [showTwitchModal, setShowTwitchModal] = useState<boolean>(false);
  const [prevTwitchConnected, setPrevTwitchConnected] =
    useState<boolean>(false);
  const [wasJustConnected, setWasJustConnected] = useState<boolean>(false);
  const [os, setOs] = useState<"mac" | "windows" | "ios" | "android" | null>(
    null
  );

  const [setupState, setSetupState] = useState<SetupState>({
    isConnecting: false,
    isDownloading: false,
    downloadProgress: 0,
    isDownloaded:
      Platform.OS === "web" &&
      !!localStorage.getItem("isKOBroadcasterDownloaded"),
    estimatedDownloadTime: "Calculating...",
    downloadSpeed: 0,
    startTime: null,
  });


  // Check if user just completed OAuth flow by checking URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get("code");

    // Twitch auth codes are 30 characters long
    if (authCode?.length === 30) {
      setWasJustConnected(true);
    }
  }, []);

  // Initialize prevTwitchConnected to current state on mount to avoid false triggers
  useEffect(() => {
    setPrevTwitchConnected(isTwitchConnected);
  }, []); // Only run once on mount

  useEffect(() => {
    isStreamConnected ? setStreamingTooltip(false) : setStreamingTooltip(true);
  }, [isStreamConnected]);

  // Check for NEW Twitch connections to show modal
  useEffect(() => {
    // Only show modal for NEW connections that happened via OAuth flow
    if (isTwitchConnected && !prevTwitchConnected && twitchUsername && wasJustConnected) {
      // This is a NEW connection via OAuth - check if modal was already shown in this session
      const modalShown =
        Platform.OS === "web"
          ? sessionStorage.getItem("twitchConnectedModalShown") === "true"
          : false;

      if (!modalShown) {
        setShowTwitchModal(true);
      }
      // Reset the flag after handling
      setWasJustConnected(false);
    }
    setPrevTwitchConnected(isTwitchConnected);
  }, [isTwitchConnected, prevTwitchConnected, twitchUsername, wasJustConnected]);

  // OS Detection
  useEffect(() => {
    if (Platform.OS === "web") {
      const p = (navigator.platform || "").toLowerCase();
      const ua = (navigator.userAgent || "").toLowerCase();
      if (p.includes("mac") || ua.includes("mac")) setOs("mac");
      else if (p.includes("win") || ua.includes("win")) setOs("windows");
      else setOs(null);
    } else {
      setOs(Platform.OS as "ios" | "android");
    }
  }, []);

  // KO Broadcaster utilities
  const updateSetupState = useCallback((key: keyof SetupState, value: any) => {
    setSetupState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const calculateEstimatedTime = useCallback((loaded: number, total: number, startTime: number) => {
    const elapsed = (Date.now() - startTime) / 1000; // seconds
    if (elapsed < 2) return "Calculating..."; // Wait a bit for more accurate estimate

    const downloadSpeed = loaded / elapsed; // bytes per second
    const remainingBytes = total - loaded;
    const remainingSeconds = remainingBytes / downloadSpeed;

    if (remainingSeconds < 60) {
      return `${Math.ceil(remainingSeconds)} seconds`;
    } else {
      const minutes = Math.ceil(remainingSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
  }, []);

  const formatFileSize = useCallback((bytes: number): string => {
    const MB = bytes / (1024 * 1024);
    if (MB < 1) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${MB.toFixed(1)} MB`;
  }, []);

  const checkDownloadStatus = useCallback(() => {
    if (Platform.OS === "web") {
      const downloaded = localStorage.getItem("isKOBroadcasterDownloaded");
      const lastDownloadTime = localStorage.getItem(
        "koBroadcasterDownloadTime"
      );

      if (downloaded && lastDownloadTime) {
        const downloadTime = new Date(lastDownloadTime).getTime();
        const now = new Date().getTime();
        const hoursSinceDownload = (now - downloadTime) / (1000 * 60 * 60);

        if (hoursSinceDownload < 24) {
          updateSetupState("isDownloaded", true);
          return true;
        } else {
          localStorage.removeItem("isKOBroadcasterDownloaded");
          localStorage.removeItem("koBroadcasterDownloadTime");
          updateSetupState("isDownloaded", false);
          return false;
        }
      }
      updateSetupState("isDownloaded", !!downloaded);
      return !!downloaded;
    }
    return false;
  }, [updateSetupState]);


  const macS3Link =
    "https://broadcaster.kallout.io/KO+Broadcaster-1.0.5-arm64.dmg";
  const windowsS3Link =
    "https://broadcaster.kallout.io/KO+Broadcaster+Setup+1.0.5.exe";
  const downloadLink =
    os === "mac" ? macS3Link : os === "windows" ? windowsS3Link : null;

  // Modal handlers
  const handleTwitchModalClose = () => {
    setShowTwitchModal(false);
  };

  const handleSkip = () => {
    console.log("User skipped Store Past Broadcasts setup");
    // Store in sessionStorage that user skipped, so modal won't show again
    if (Platform.OS === "web") {
      sessionStorage.setItem("twitchConnectedModalShown", "true");
    }
    // Close the modal
    setShowTwitchModal(false);
  };

  const handleGoToEventPage = () => {
    // Navigate to MyEvents section
    if (Platform.OS === "web") {
      window.location.href = "/events";
    }
    // Close the modal
    setShowTwitchModal(false);
  };

  const handleConnectToBroadcaster = useCallback(() => {
    if (Platform.OS !== "web") {
      toast.error(
        "Please use a desktop (macOS/Windows) to install the broadcaster."
      );
      return;
    }
    if (!downloadLink) {
      toast.error("Unsupported operating system");
      return;
    }

    // Direct download without XMLHttpRequest to avoid CORS issues
    updateSetupState("isConnecting", true);
    updateSetupState("isDownloading", true);

    try {
      // Create direct download link
      const fileName = os === "mac"
        ? "KO Broadcaster-1.0.5-arm64.dmg"
        : "KO Broadcaster Setup 1.0.5.exe";

      const linkElement = document.createElement("a");
      linkElement.href = downloadLink;
      linkElement.download = fileName;
      linkElement.target = "_blank"; // Fallback to open in new tab
      linkElement.style.display = "none";
      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);

      // Simulate download completion
      setTimeout(() => {
        updateSetupState("isConnecting", false);
        updateSetupState("isDownloading", false);
        updateSetupState("downloadProgress", 100);

        localStorage.setItem("isKOBroadcasterDownloaded", "true");
        localStorage.setItem("koBroadcasterDownloadTime", new Date().toISOString());
        updateSetupState("isDownloaded", true);

        toast.success("Download started! Install and run KO Broadcaster after download completes.");
      }, 1000);

    } catch (error) {
      console.error("Download failed:", error);
      updateSetupState("isConnecting", false);
      updateSetupState("isDownloading", false);
      toast.error("Download failed. Please contact support or try again.");
    }
  }, [downloadLink, os, updateSetupState]);

  return (
    <View style={{ position: "relative", width: "100%" }}>
      <View style={{ width: "100%" }}>
        <StreamingPlateformComponent
          accessToken={accessToken}
          socialMediaPlatforms={socialMediaPlatforms}
          streamingTooltip={streamingTooltip}
          setStreamingTooltip={setStreamingTooltip}
          platformStatus={{
            YouTube: isYouTubeConnected ? "Connected" : "Not connected",
            Twitch: isTwitchConnected ? "Connected" : "Not connected",
            Squadifi: "Not connected",
            twitchUsername,
            youtubeUsername,
            squadifiUsername,
          }}
          fetchStatus={fetchStatus}
          redirectToYouTubeOAuth={redirectToYouTubeOAuth}
          redirectToTwitchOAuth={redirectToTwitchOAuth}
          twitchUsername={twitchUsername}
          youtubeUsername={youtubeUsername}
          squadifiUsername={squadifiUsername}
          loadingState={loadingState}
          isLoading={isInitialLoading}
        />

        {/* KO Broadcaster Section */}
        <View style={{ marginTop: 20 }}>
          {(deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM ? (
            /* Mobile: Show simplified Broadcaster Software section with desktop message */
            <View
              style={{
                padding: 8,
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 6,
                alignSelf: "stretch",
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                backgroundColor: "rgba(31, 41, 55, 0.5)",
              }}
            >
              <Text style={{ color: "#FFF", fontSize: 14, fontWeight: "700" }}>
                KO Broadcaster
              </Text>
              {/* Separator line */}
              <View style={{ height: 1, backgroundColor: "#40324C", alignSelf: "stretch" }} />
              <Text style={{ color: "#FFF", fontSize: 14, fontWeight: "500" }}>
                KO Broadcaster is our official streaming software
              </Text>
              <Text style={{ color: "#FFF", fontSize: 12, fontWeight: "400" }}>
                It only takes 2 minutes to download.{"\n"}
                Download and install the KO Broadcaster, and we will handle all for you
              </Text>
              {/* Mobile Warning Message */}
              <View
                style={{
                  padding: 6,
                  paddingHorizontal: 8,
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  alignSelf: "stretch",
                  borderRadius: 8,
                  backgroundColor: "#545FC7",
                  marginTop: 10,
                }}
              >
                <Text style={{ color: "#FFF", fontSize: 12, fontWeight: "600" }}>
                  Please use desktop, this feature is not available in mobile app
                </Text>
              </View>
            </View>
          ) : (
            /* Desktop: Show full KOBroadcasterComponent */
            <KOBroadcasterComponent
              handleConnectToBroadcaster={handleConnectToBroadcaster}
              setupState={setupState}
              checkDownloadStatus={checkDownloadStatus}
            />
          )}
        </View>
      </View>

      {/* Steps tooltip - Desktop only */}
      {!streamingTooltip && !((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) && (
        <View
          style={{
            position: "absolute",
            top: 0,
            right: 10,
            width: "30%",
            zIndex: 1000,
          }}
        >
          <View
            style={{
              backgroundColor: AppColors.purple_453B50,
              padding: 24,
              gap: 24,
              borderRadius: 10,
            }}
          >
            <Text style={[AppFontStyle.BOLD_ANYBODY_18, { color: "white" }]}>
              Steps
            </Text>

            {/* Step 1: Platform Connected */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              {isStreamConnected ? (
                <Image
                  source={AppImages.icCompleteGreen}
                  style={{ width: 18, height: 18 }}
                />
              ) : (
                <View
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 9,
                    justifyContent: "center",
                    alignItems: "center",
                    borderColor: "#6B7280",
                    borderWidth: 2,
                  }}
                />
              )}
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_12,
                  {
                    color: isStreamConnected ? "white" : "#6B7280",
                  },
                ]}
              >
                Step1: Platform Connected (
                {(isYouTubeConnected ? 1 : 0) + (isTwitchConnected ? 1 : 0)})
              </Text>
            </View>

            {/* Step 2: Connect KO Broadcaster */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              {isKOBroadcasterConnected ? (
                <Image
                  source={AppImages.icCompleteGreen}
                  style={{ width: 18, height: 18 }}
                />
              ) : (
                <View
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 9,
                    justifyContent: "center",
                    alignItems: "center",
                    borderColor: "#6B7280",
                    borderWidth: 2,
                  }}
                />
              )}
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_12,
                  {
                    color: isKOBroadcasterConnected ? "white" : "#6B7280",
                  },
                ]}
              >
                Step2: Connect KO Broadcaster
              </Text>
            </View>

            {/* Setup Progress */}
            <View style={{ gap: 12 }}>
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Text
                  style={[
                    AppFontStyle.SEMIBOLD_ANYBODY_12,
                    { color: "#6B7280" },
                  ]}
                >
                  Setup Progress
                </Text>
                <Text
                  style={[AppFontStyle.SEMIBOLD_ANYBODY_12, { color: "white" }]}
                >
                  {(isStreamConnected ? 1 : 0) +
                    (isKOBroadcasterConnected ? 1 : 0)}
                  /2
                </Text>
              </View>
              <View
                style={{
                  height: 8,
                  backgroundColor: "#374151",
                  borderRadius: 8,
                }}
              >
                <View
                  style={{
                    width: `${((isStreamConnected ? 1 : 0) +
                      (isKOBroadcasterConnected ? 1 : 0)) *
                      50
                      }%`,
                    height: "100%",
                    backgroundColor: "#3B82F6",
                    borderRadius: 8,
                  }}
                />
              </View>
            </View>
          </View>
        </View>
      )}

      {/* Enable Store Past Broadcasts - Below Steps Panel */}
      {!streamingTooltip && isTwitchConnected && twitchUsername && (
        <View
          style={{
            position:
              (deviceSize as string) === DEVICE_SIZES.XS ||
                (deviceSize as string) === DEVICE_SIZES.SM
                ? "relative"
                : "absolute",
            top:
              (deviceSize as string) === DEVICE_SIZES.XS ||
                (deviceSize as string) === DEVICE_SIZES.SM
                ? 0
                : 220, // Position below the steps panel
            right:
              (deviceSize as string) === DEVICE_SIZES.XS ||
                (deviceSize as string) === DEVICE_SIZES.SM
                ? 0
                : 10,
            width:
              (deviceSize as string) === DEVICE_SIZES.XS ||
                (deviceSize as string) === DEVICE_SIZES.SM
                ? "100%"
                : "30%",
            marginTop:
              (deviceSize as string) === DEVICE_SIZES.XS ||
                (deviceSize as string) === DEVICE_SIZES.SM
                ? 20
                : 0,
            zIndex: 999,
          }}
        >
          <View
            style={{
              backgroundColor: AppColors.purple_453B50,
              padding: 24,
              borderRadius: 10,
            }}
          >
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_16,
                { color: "white", marginBottom: 12 },
              ]}
            >
              Enable 'Store Past Broadcasts'
            </Text>

            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_14,
                { color: AppColors.white, marginBottom: 16 },
              ]}
            >
              To make sure your streams are saved automatically and can also
              appear inside Kallout, you need to enable Twitch's 'Store Past
              Broadcasts' option
            </Text>

            {/* VOD Settings Image */}
            <Image
              source={AppImages.Store_past_Stream_White}
              style={{
                width: 186,
                height: 70,
                marginBottom: 16,
              }}
              resizeMode="contain"
            />

            {/* Instructions */}
            <View style={{ gap: 8, marginBottom: 16 }}>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { color: AppColors.white },
                ]}
              >
                1. Open Twitch Settings
              </Text>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { color: AppColors.white },
                ]}
              >
                2. Go to Stream → VOD Settings
              </Text>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { color: AppColors.white },
                ]}
              >
                3. Turn ON Store Past Broadcasts
              </Text>
            </View>

            {/* Open Settings Button */}
            <CommonButton
              title="Open Twitch settings"
              onTap={() => {
                if (Platform.OS === "web" && twitchUsername) {
                  window.open(
                    `https://dashboard.twitch.tv/u/${twitchUsername}/settings/stream`,
                    "_blank"
                  );
                }
              }}
              buttonStyle={{
                backgroundColor: AppColors.appViolentColor_6D08C3,
                borderRadius: 8,
                paddingVertical: 12,
                paddingHorizontal: 20,

                alignSelf: "flex-start",
              }}
              fontStyle={[
                AppFontStyle.SEMIBOLD_ANYBODY_14,
                { color: AppColors.white },
              ]}
            />
          </View>
        </View>
      )}

      {/* Twitch Connected Modal */}
      <TwitchConnectedModal
        isVisible={showTwitchModal}
        onClose={handleTwitchModalClose}
        twitchUsername={twitchUsername || ""}
        onSkip={handleSkip}
        onGoToEventPage={handleGoToEventPage}
      />
    </View>
  );
};

export default memo(StreamSettingsTabComponent);
